import React, {
  useState,
  useEffect,
  useRef,
  MutableRefObject,
  useCallback,
  ReactElement,
} from 'react'
import type {
  AutomationTarget,
  SlottableComponent,
} from '../../../../src/types'
// @ts-ignore next
import BezierEasing from 'bezier-easing'
import { useTransition, animated } from '@react-spring/web'
import { DEFAULT_LARGE_TRANSITION_DURATION_MS } from '../../../constants/statics'
import RankItemWrapper from './rankItemWrapper'
import classnames from 'classnames/bind'
import styles from './styles.scss'
const cx = classnames.bind(styles)

const NEW_ITEM_DEFAULT_HEIGHT = 100

/**
 * Items are expected to be a SlottableComponent AND have an id prop.
 * The id prop is mandatory, as it is used to track the items (additions, reorders, etc.)
 */
export type RankListItem<S extends string = string> = ReactElement<{
  id: string
}> &
  SlottableComponent<S>

type RankItemData = {
  id: string
  ref: MutableRefObject<HTMLDivElement | null>
  content: RankListItem
}

interface RankListProps extends AutomationTarget {
  className?: string
  itemData: RankListItem[]
  transitionDurationMs?: number
}

const RankList: React.FunctionComponent<RankListProps> = ({
  className,
  elmId = '',
  itemData,
  transitionDurationMs = DEFAULT_LARGE_TRANSITION_DURATION_MS as number,
}: RankListProps) => {
  const refs = useRef<React.MutableRefObject<HTMLDivElement | null>[]>([])
  const [rankItems, setRankItems] = useState<RankItemData[]>([])
  const [trigger, setTrigger] = useState(0)

  const getRankItemHeight = useCallback((elm: HTMLDivElement | null) => {
    if (!elm) return NEW_ITEM_DEFAULT_HEIGHT
    const style = window.getComputedStyle(elm)
    const margin =
      (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0)
    return Math.ceil(elm.offsetHeight + margin)
  }, [])

  /**
   * When itemData changes, update the rankItems array by comparing the id's.
   * If there's a match, keep the item, if not, create a new item with the ref and content.
   */
  useEffect(() => {
    let contentChanged = false
    const newItems = itemData.map(item => {
      const existingRankItem = rankItems.find(i => i.id === item.props.id)
      if (existingRankItem) {
        // Check if the content has changed
        if (existingRankItem.content !== item) {
          contentChanged = true
          return { ...existingRankItem, content: item }
        }
        return { ...existingRankItem }
      } else {
        const newRef = { current: null }
        refs.current.push(newRef)
        return { id: item.props.id, ref: newRef, content: item }
      }
    })

    /**
     * These triggers are necessary to trigger the transition effect when only the content changes, especially in
     * the case of ExpandableCard, which will dynamically change its height based on user interaction.
     */
    if (contentChanged) {
      setTimeout(() => {
        setTrigger(prev => prev + 1)
      }, 1)
      setTimeout(() => {
        setTrigger(prev => prev + 1)
      }, transitionDurationMs)
    }

    setRankItems(prev => {
      if (prev.length !== newItems.length) {
        // trigger a re-render to get the correct height after new one renders
        setTrigger(prev => prev + 1)
      }
      return newItems
    })
  }, [itemData, trigger]) // eslint-disable-line react-hooks/exhaustive-deps

  /**
   *
   */
  useEffect(() => {
    setTrigger(prev => prev + 1)
  }, [refs])

  /**
   * Animation stuff...
   */
  let height = 0

  const transitions = useTransition(
    rankItems.map(item => {
      const itemHeight = getRankItemHeight(item.ref.current)
      return {
        ...item,
        height: itemHeight,
        y: (height += itemHeight) - itemHeight,
      }
    }),
    {
      key: (item: RankItemData) => item.id,
      from: { height: 0, opacity: 0 },
      leave: { height: 0, opacity: 0 },
      enter: ({ y, height }) => ({ y, height, opacity: 1 }),
      update: ({ y, height }) => ({ y, height }),
      config: {
        duration: transitionDurationMs,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        easing: BezierEasing(0.5, -0.1, 0.5, 1.2),
      },
    }
  )

  return (
    <div
      className={cx('list', className)}
      data-elm-id={elmId}
      style={{ height }}
    >
      {transitions((style, item, _t, index) => (
        <animated.div
          key={item.id}
          className={cx('animated-item')}
          style={{ zIndex: rankItems.length - index, ...style }}
        >
          <RankItemWrapper id={item.id} ref={item.ref}>
            {item.content}
          </RankItemWrapper>
        </animated.div>
      ))}
    </div>
  )
}

export default RankList
